import Highcharts from 'highcharts';
import { TimelineAxisTypes, type TimelineDateSeries, TimelineSeriesCustomData } from '../../../shared';

export type DateRangeSeriesDataItem = {
    label: string;
    value: {
        Start: string;
        End: string;
    };
};

type Block = {
    label: string;
    start: number;
    end: number;
};

type MapDateRangeSeriesOptions = {
    series: TimelineDateSeries;
    /**
     * Gets y offset for data points. Calling it multiple times should result in incrementing values.
     */
    getYOffset: () => number;
};
export function mapDateRangeSeries({
    series,
    getYOffset,
}: MapDateRangeSeriesOptions): Highcharts.SeriesXrangeOptions & TimelineSeriesCustomData {
    const lanes: Block[][] = [];

    // Map blocks and sort by start date
    const allBlocks = (series.data as DateRangeSeriesDataItem[])
        .map((d) => {
            const startAsDate = new Date(d.value.Start);
            const endAsDate = new Date(d.value.End);

            // If Start and End are the same instant, treat it as the full UTC day.
            // We use setUTCHours() to expand to 00:00–23:59 without rebuilding the date,
            // because re-parsing/formatting can shift the month/day due to timezone rules.
            if (startAsDate.getTime() === endAsDate.getTime()) {
                startAsDate.setUTCHours(0, 0, 0, 0);
                endAsDate.setUTCHours(23, 59, 59, 999);
            }

            return {
                label: d.label,
                start: startAsDate.getTime(),
                end: endAsDate.getTime(),
            };
        })
        // Remove bad data (invalid dates, start date after end date)
        .filter((b) => !isNaN(b.start) && !isNaN(b.end) && b.start <= b.end)
        .toSorted((a, z) => a.start - z.start);

    // Group blocks into lanes based on which can fit
    allBlocks.forEach((b) => {
        // Find a lane that can fit the block, if possible
        const matchedLane = lanes.find((blocks) => {
            // Since the blocks are in start date order, this will have the latest end date of any blocks in the lane
            const latestBlock = blocks.at(-1);

            // If there's no "last block", then the lane must be empty, so it has room...
            if (latestBlock == null) {
                return true;
            }
            // If the current block starts after lanes latest block ends, the current block can fit
            return b.start >= latestBlock.end;
        });

        // Add block to lane if it fits. Otherwise, create new lane
        if (matchedLane != null) {
            matchedLane.push(b);
        } else {
            lanes.push([b]);
        }
    });

    // Map lanes of blocks into highcharts xrange chart data points
    const data = lanes.flatMap((blocks) => {
        const y = getYOffset(); // Get new y value for each lane

        return blocks.map(({ label, start, end }) => ({
            name: label,
            x: start,
            x2: end,
            y,
        }));
    });

    return {
        type: 'xrange',
        data,
        stickyTracking: false,
        custom: {
            axisType: TimelineAxisTypes.Date,
        },
    };
}
